import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';

interface Props {
    ecode: string;
    phoneNumber: string | null;
    onVerify: (otp: string) => void;
    onBack: () => void;
    onResendOTP: () => void;
    isLoading: boolean;
    error: string;
}

const OTPStep: React.FC<Props> = ({
    ecode,
    phoneNumber,
    onVerify,
    onBack,
    onResendOTP,
    isLoading,
    error,
}) => {
    const [otp, setOtp] = useState('');
    const [countdown, setCountdown] = useState(60);

    useEffect(() => {
        if (countdown <= 0) return;

        const timer = setTimeout(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [countdown]);

    const canResend = countdown <= 0;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (otp.length !== 6) return;

        onVerify(otp);
    };

    const handleResend = async () => {
        await onResendOTP();

        setCountdown(60);
        setOtp('');
    };

    return (
        <div className="w-full max-w-sm border rounded-lg p-6 shadow-sm bg-card">
            <div className="mb-6 text-center">
                <h1 className="text-lg font-semibold">Verify OTP</h1>

                <p className="text-sm text-muted-foreground">Employee Code: {ecode}</p>

                {phoneNumber && (
                    <p className="text-sm text-muted-foreground">
                        OTP sent to ******{phoneNumber}
                    </p>
                )}
            </div>

            {error && (
                <div className="mb-4 text-sm text-red-600">
                    {error}
                </div>
            )}

            <form
                onSubmit={handleSubmit}
                className="space-y-4"
            >
                <div className="space-y-2">
                    <label className="text-sm font-medium">
                        OTP
                    </label>

                    <Input
                        value={otp}
                        onChange={(e) =>
                            setOtp(
                                e.target.value.replace(/\D/g, '').slice(0, 6)
                            )
                        }
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        disabled={isLoading}
                    />
                </div>

                <Button
                    type="submit"
                    className="w-full"
                    disabled={otp.length !== 6 || isLoading}
                >
                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                </Button>

                <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={onBack}
                >
                    Back
                </Button>
            </form>

            <div className="pt-4 text-center text-sm">
                {canResend ? (
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={isLoading}
                        className="font-medium text-blue-600 hover:text-blue-700"
                    >
                        Resend OTP
                    </button>
                ) : (
                    <span>Resend OTP in {countdown}s</span>
                )}
            </div>
        </div>
    );
};

export default OTPStep;